/**
 * Formats a numeric amount for display: whole values without trailing .00,
 * fractional values with two decimal places (e.g. 10 -> "10", 10.5 -> "10.50").
 */
export function formatDisplayCurrencyAmount(amount: number | string): string {
  const numericAmt = Number(amount);
  if (!Number.isFinite(numericAmt)) {
    return String(amount);
  }
  return Number.isInteger(numericAmt) ? String(numericAmt) : numericAmt.toFixed(2);
}

/** Normalises decimal numbers embedded in CMS/DV strings after placeholder replacement. */
export function formatDecimalNumbersInString(originalString: string): string {
  if (!originalString) {
    return originalString;
  }
  return originalString.replace(/\d+\.\d+/g, (match) => formatDisplayCurrencyAmount(parseFloat(match)));
}

/** Common ISO currency code → display symbol used in sales HTML. */
const DEFAULT_CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: "£",
  USD: "$",
  EUR: "€",
  CAD: "$",
  AUD: "$",
  NZD: "$",
};

/** Currency symbols commonly baked into partialreg optmsg / paymentHtml amounts. */
const AMOUNT_CURRENCY_TOKEN_REGEX = /(?:CA\$|A\$|US\$|NZ\$|[$€£])\s?\d/g;

export function getDefaultCurrencySymbol(currencyCode?: string | null): string {
  if (!currencyCode) {
    return "£";
  }
  return DEFAULT_CURRENCY_SYMBOLS[String(currencyCode).toUpperCase()] ?? "£";
}

export function normalizeGetRegOptMsgHtml(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value) && typeof value[0] === "string") {
    return value[0];
  }
  return "";
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** True when HTML contains the symbol immediately before a numeric amount. */
export function htmlHasAmountWithCurrencySymbol(html: string, symbol: string): boolean {
  if (!html || !symbol) {
    return false;
  }
  return new RegExp(`${escapeRegExp(symbol)}\\s?\\d`).test(html);
}

/**
 * True when HTML contains amount prefixes using a currency symbol other than expected.
 * Needed because paymentHtml can mix a correct jackpot (£24M) with wrong prices ($1).
 */
export function htmlHasForeignCurrencyAmount(html: string, expectedSymbol: string): boolean {
  if (!html || !expectedSymbol) {
    return false;
  }
  const matches = html.match(AMOUNT_CURRENCY_TOKEN_REGEX) || [];
  return matches.some((token) => !token.startsWith(expectedSymbol));
}

/**
 * Rewrites leading currency symbols before amounts (e.g. `$1` → `£1`) to the target symbol.
 * Leaves non-amount `$` characters alone when not followed by a digit.
 */
export function alignCurrencyAmountSymbols(html: string, targetSymbol: string): string {
  if (!html || !targetSymbol) {
    return html;
  }
  return html.replace(
    /(^|[^A-Za-z0-9])(?:CA\$|A\$|US\$|NZ\$|[$€£])\s?(?=\d)/g,
    `$1${targetSymbol}`
  );
}

export interface PartialRegPaymentHtmlSource {
  paymentHtml?: string;
  getRegOptMsgHtml?: string | string[];
  effectiveLocale?: { currency?: string; language?: string };
  userDefaults?: { currency?: string; language?: string };
}

/**
 * Picks payment/optmsg HTML that matches effectiveLocale currency.
 * partialregv2 has been observed returning paymentHtml with `$` while
 * effectiveLocale.currency / getRegOptMsgHtml are GBP/`£`.
 */
export function resolveLocaleAlignedPaymentHtml(
  pageData: PartialRegPaymentHtmlSource | null | undefined,
  fallbackCurrency = "GBP"
): string {
  const paymentHtml = pageData?.paymentHtml || "";
  const optHtml = normalizeGetRegOptMsgHtml(pageData?.getRegOptMsgHtml);
  const currency = String(
    pageData?.effectiveLocale?.currency ||
      pageData?.userDefaults?.currency ||
      fallbackCurrency
  ).toUpperCase();
  const targetSymbol = getDefaultCurrencySymbol(currency);

  if (!paymentHtml) {
    return optHtml;
  }

  const paymentHasForeign = htmlHasForeignCurrencyAmount(paymentHtml, targetSymbol);
  const optIsClean =
    !!optHtml &&
    htmlHasAmountWithCurrencySymbol(optHtml, targetSymbol) &&
    !htmlHasForeignCurrencyAmount(optHtml, targetSymbol);

  if (paymentHasForeign && optIsClean) {
    return optHtml;
  }

  if (paymentHasForeign) {
    return alignCurrencyAmountSymbols(paymentHtml, targetSymbol);
  }

  return paymentHtml;
}
